import type { LinkedDoor } from './contract.js';

/**
 * LA PORTE OUVERTE DANS UNE FICHE (chantier `porte-fiche`, 13/10/2026) : le
 * clic sur un projet montre les taches de CE projet, et non sa fiche.
 *
 * Une porte qui declare `auClic` (`ScreenDoor.auClic`) ENTRE, au clic sur une
 * fiche, dans la porte de son lien, FILTREE sur la fiche cliquee. Ce module
 * porte les regles pures de ce chemin, sans React : l'adresse, la resolution,
 * le filtre et les libelles des deux boutons de retour. Le moteur
 * (`useScreen.ts`) les cable ; le detail : `docs/socle/porte-fiche.md`.
 */

/**
 * LE CHEMIN D'ENTREE : la fiche PARENTE (`id`), la porte ou elle vit
 * (`porte`), et le lien de cette porte par lequel on est entre (`lien`).
 * Il voyage dans `ScreenQuery.dans` et dans l'adresse (`dans`).
 */
export interface DansUneFiche {
  porte: string;
  lien: string;
  id: string;
}

/**
 * LE SEPARATEUR D'ADRESSE. Un id de porte et un id de lien sont en minuscules,
 * chiffres et tirets (schema `app-screens.ts`) : ils ne contiennent jamais `~`.
 * L'id de la fiche vient EN DERNIER et garde tout ce qui suit.
 */
const SEPARATEUR = '~';

/** Le parametre `dans`, lu. Tout ce qui n'a pas trois morceaux non vides vaut « absent ». */
export function lireDansParam(raw: string | undefined): DansUneFiche | null {
  if (raw == null || raw === '') return null;
  const premier = raw.indexOf(SEPARATEUR);
  const second = premier < 0 ? -1 : raw.indexOf(SEPARATEUR, premier + 1);
  if (premier <= 0 || second <= premier + 1 || second === raw.length - 1) return null;
  return { porte: raw.slice(0, premier), lien: raw.slice(premier + 1, second), id: raw.slice(second + 1) };
}

/** Le parametre `dans`, ecrit : `undefined` hors du chemin (une adresse ordinaire ne change pas d'un octet). */
export function ecrireDansParam(dans: DansUneFiche | null | undefined): string | undefined {
  return dans == null ? undefined : [dans.porte, dans.lien, dans.id].join(SEPARATEUR);
}

/** Deux chemins identiques ? `null` et `undefined` se valent. */
export function memeDans(a: DansUneFiche | null | undefined, b: DansUneFiche | null | undefined): boolean {
  if (a == null || b == null) return a == b;
  return a.porte === b.porte && a.lien === b.lien && a.id === b.id;
}

/** Ce qu'il faut savoir d'une porte pour resoudre un chemin. */
interface PorteDuChemin {
  id: string;
  label: string;
  links?: readonly LinkedDoor[];
}

/**
 * LE CHEMIN, RESOLU sur le catalogue de portes, ou `null` s'il ne tient pas.
 *
 * DEUX PLACES, ET AUCUNE AUTRE : dans la porte LIEE (`'enfant'` : la
 * collection filtree, et la fiche d'un enfant), ou dans la porte PARENTE sur
 * la fiche parente elle-meme (`'parent'` : « Fiche du projet » ouverte depuis
 * le chemin). Partout ailleurs, le chemin ne veut rien dire : le moteur le
 * laisse tomber.
 */
export function resoudreDans<D extends PorteDuChemin>(
  portes: readonly D[],
  dans: DansUneFiche | null | undefined,
  etat: { section: string | null; openId: string | null },
): { dans: DansUneFiche; lien: LinkedDoor; parente: D; enfant: D; place: 'enfant' | 'parent' } | null {
  if (dans == null) return null;
  const parente = portes.find((porte) => porte.id === dans.porte);
  const lien = parente?.links?.find((candidat) => candidat.id === dans.lien);
  const enfant = lien === undefined ? undefined : portes.find((porte) => porte.id === lien.door);
  if (parente === undefined || lien === undefined || enfant === undefined) return null;
  if (etat.section === lien.door) return { dans, lien, parente, enfant, place: 'enfant' };
  if (etat.section === dans.porte && (etat.openId == null || etat.openId === dans.id)) {
    return { dans, lien, parente, enfant, place: 'parent' };
  }
  return null;
}

/**
 * LA VALEUR D'UN CHEMIN DE STOCKAGE (`projectId`, `customFields.projetId`).
 * Le chemin vient de `LinkedDoor.by`, deja resolu : il n'y a rien a deviner.
 */
export function valeurAuChemin(fiche: unknown, chemin: string): unknown {
  let courant: unknown = fiche;
  for (const morceau of chemin.split('.')) {
    if (courant == null || typeof courant !== 'object') return undefined;
    courant = (courant as Record<string, unknown>)[morceau];
  }
  return courant;
}

/**
 * LES FICHES DE LA PORTE LIEE QUI DESIGNENT LE PARENT : le filtre du chemin.
 * Il s'applique sur ce que le chargeur rend, comme la tranche d'une porte, et
 * c'est ce qui fait suivre les stats, la recherche et les filtres.
 */
export function fichesDuParent<T>(fiches: readonly T[], chemin: string, id: string): T[] {
  return fiches.filter((fiche) => valeurAuChemin(fiche, chemin) === id);
}

/** « de Projet Test », « d'Atelier » : l'elision devant une voyelle ou un h. */
function deTitre(titre: string): string {
  return /^[aeiouyhàâäéèêëîïôöùûü]/i.test(titre) ? `d’${titre}` : `de ${titre}`;
}

/**
 * LES LIBELLES DU CHEMIN : toujours « <vue> : <ce qu'on y voit> ». La seule
 * donnee qui y entre est le TITRE de la fiche parente, comme la puce d'une
 * fiche arrivee par un lien (l'exception de G13 : le mot dit ou l'on retombe).
 */
export const libellesDuChemin = {
  /** Le retour au debut : « Liste : Projets ». */
  debut(vue: string, porte: string): string {
    return `${vue} : ${porte}`;
  },
  /** Ce que la porte liee montre : « Tâches de Projet Test ». */
  contenu(porte: string, titre: string): string {
    return titre === '' ? porte : `${porte} ${deTitre(titre)}`;
  },
  /** Le retour a l'etape d'avant : « Kanban : Tâches de Projet Test ». */
  etape(vue: string, porte: string, titre: string): string {
    return `${vue} : ${libellesDuChemin.contenu(porte, titre)}`;
  },
};

/**
 * CE QUE LE GABARIT RECOIT (`ScreenState.chemin`), deja decide : il ne fait que
 * rendre. `null` hors du chemin, et l'ecran garde alors exactement ses boutons
 * d'avant.
 */
export interface CheminDePorte {
  /**
   * Le titre de la zone 2 dans la porte liee : « Tâches de Projet Test ».
   * `null` sur la fiche parente, qui garde le titre de sa porte.
   */
  titre: string | null;
  /** La porte que le menu allume : la PARENTE, on est « dans un projet ». */
  porteDuMenu: string;
  /**
   * Le retour au debut : la collection de la porte parente. `court` est le mot
   * du telephone, sans la vue (« Projets ») : a 390 px, « Kanban : » mangeait
   * la place du nom.
   */
  debut: { libelle: string; court: string; aller(): void };
  /**
   * L'etape d'avant, sur une fiche du chemin : le meme mot que la puce de
   * sortie (`ScreenState.sortie`), et son mot court (« Tâches de Projet Test »).
   * `null` sur la collection.
   */
  etape: { libelle: string; court: string } | null;
  /**
   * L'acces a la fiche parente, sur la collection filtree seulement : le
   * bouton secondaire « Ouvrir fiche » (retour du fondateur du 13/10/2026),
   * `titre` dit laquelle au survol.
   */
  parent: { titre: string; ouvrir(): void } | null;
}
